import React, { useEffect } from "react";
import { useQueryStates, parseAsInteger, parseAsString } from "nuqs";
import { IProduct } from "../../../types/api/product";
import {
  useProductList,
  useProductDelete,
  useProductApprove,
  useProductDetail,
} from "../../../hooks/api/product/hooks";
import { ProductPanel } from "./ProductPanel";
import ProductModal from "./ProductModal";
import { useModalStore } from "../../../store/slices/modal";
import { MODAL_TYPES } from "../../../types/modal-types";

import { useMessageApi } from "../../../provider/AntdNotification";

const ProductList: React.FC = () => {
  const messageApi = useMessageApi();

  // Single qData state object managed by nuqs useQueryStates
  const [qData, setQData] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    limit: parseAsInteger.withDefault(10),
    search: parseAsString.withDefault(""),
    status: parseAsString.withDefault(""),
    sortBy: parseAsString.withDefault(""),
    // Opens straight into a specific sculpture's edit modal - the
    // "sculpture needs review" email links here so whoever picks it up
    // doesn't have to search the list first (Oliver's testing doc, round
    // 7).
    edit: parseAsString.withDefault(""),
    // Set once a deep-linked product resolves (below) so the list behind
    // the modal is already scoped to that artist's other sculptures by
    // the time it closes, instead of dropping back to the full
    // unfiltered catalog - Peter, Sept 2026: saving a product opened this
    // way meant having to search for the same artist all over again.
    artistId: parseAsString.withDefault(""),
  });

  const queryParams = {
    page: qData.page,
    limit: qData.limit,
    search: qData.search || undefined,
    status: qData.status || undefined,
    sortBy: qData.sortBy || undefined,
    artistId: qData.artistId || undefined,
  };

  const { type, data: selectedProduct, open, close } = useModalStore();

  const { data, isLoading, isError } = useProductList(queryParams);

  const { data: deepLinkedProduct } = useProductDetail(qData.edit, {
    enabled: !!qData.edit,
  });

  useEffect(() => {
    if (qData.edit && deepLinkedProduct?.data) {
      open(MODAL_TYPES.EDIT_PRODUCT, deepLinkedProduct.data);
      setQData({
        edit: null,
        artistId: deepLinkedProduct.data.artistId || null,
        page: 1,
        search: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qData.edit, deepLinkedProduct]);

  const deleteMutation = useProductDelete({
    qData: queryParams,
    onSuccess: () => messageApi.success("Sculpture deleted successfully"),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to delete sculpture"),
  });

  const approveMutation = useProductApprove({
    qData: queryParams,
    onSuccess: () => messageApi.success("Sculpture approved and published"),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to approve sculpture"),
  });

  const handleAdd = () => {
    open(MODAL_TYPES.CREATE_PRODUCT);
  };

  const handleEdit = (record: IProduct) => {
    open(MODAL_TYPES.EDIT_PRODUCT, record);
  };

  const handleEditVariants = (record: IProduct) => {
    open(MODAL_TYPES.EDIT_PRODUCT_VARIANTS, record);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleApprove = (record: IProduct) => {
    approveMutation.mutate(record._id);
  };

  return (
    <div className="w-full">
      <ProductPanel
        title={
          <h1 className="text-xl font-semibold text-slate-800">Sculptures</h1>
        }
        addLabel="Add Sculpture"
        list={data?.data || []}
        total={data?.extra?.total || 0}
        loading={isLoading}
        isError={isError}
        queryData={queryParams}
        artistFilterName={
          qData.artistId ? data?.data?.[0]?.artistName || "this artist" : undefined
        }
        onClearArtistFilter={() => setQData({ artistId: null, page: 1 })}
        onSearchChange={(v) => {
          setQData({ search: v || null, page: 1 });
        }}
        onStatusChange={(v) => {
          setQData({ status: v || null, page: 1 });
        }}
        onSortChange={(v) => {
          setQData({ sortBy: v || null, page: 1 });
        }}
        onPageChange={(page, limit) => {
          setQData(
            limit !== queryParams.limit ? { page: 1, limit } : { page },
          );
        }}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onEditVariants={handleEditVariants}
        onDelete={handleDelete}
        onApprove={handleApprove}
      />
      <ProductModal
        isOpen={
          type === MODAL_TYPES.CREATE_PRODUCT ||
          type === MODAL_TYPES.EDIT_PRODUCT ||
          type === MODAL_TYPES.EDIT_PRODUCT_VARIANTS
        }
        onClose={close}
        selectedItem={selectedProduct}
        qData={queryParams}
      />
    </div>
  );
};

export default ProductList;
